<template>
  <v-card class="mb-4">
    <v-card-title class="d-flex justify-space-between align-center py-3">
      <div class="d-flex align-center">
        <v-icon
          color="primary"
          class="mr-2"
        >
          {{ icons.mdiChartTimeline }}
        </v-icon>
        <span>来店パターン</span>
      </div>
      <v-btn
        small
        outlined
        color="primary"
        :loading="isLoading"
        @click="showDetailDialog = true"
      >
        詳細を見る
      </v-btn>
    </v-card-title>

    <v-card-text>
      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
        class="mb-4"
      />

      <template v-else>
        <!-- サマリー -->
        <div class="d-flex justify-space-around mb-4">
          <div class="text-center">
            <div class="text-h4 font-weight-bold primary--text">
              {{ summary.totalVisits }}
            </div>
            <div class="text-caption">
              総来店数
            </div>
          </div>
          <div
            v-if="peakDayInfo"
            class="text-center"
          >
            <div class="text-h4 font-weight-bold success--text">
              {{ peakDayInfo.label }}
            </div>
            <div class="text-caption">
              最多曜日 ({{ peakDayInfo.count }}回)
            </div>
          </div>
          <div
            v-if="peakHourInfo"
            class="text-center"
          >
            <div class="text-h4 font-weight-bold info--text">
              {{ peakHourInfo.label }}
            </div>
            <div class="text-caption">
              最多時間帯 ({{ peakHourInfo.count }}回)
            </div>
          </div>
        </div>

        <!-- 曜日別棒グラフ（簡易表示） -->
        <div class="day-chart">
          <div
            v-for="day in byDayOfWeek"
            :key="day.dayOfWeek"
            class="day-bar-container"
          >
            <div
              class="day-bar"
              :style="{ height: getBarHeight(day.count) }"
            />
            <div class="day-label">
              {{ day.label }}
            </div>
          </div>
        </div>
      </template>
    </v-card-text>

    <!-- 詳細ダイアログ -->
    <v-dialog
      v-model="showDetailDialog"
      max-width="900"
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <span>来店パターン詳細</span>
          <v-btn
            icon
            @click="showDetailDialog = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle>
          期間: {{ formatDate(startDate) }} 〜 {{ formatDate(endDate) }}
        </v-card-subtitle>
        <v-card-text>
          <v-tabs v-model="activeTab">
            <v-tab>曜日別</v-tab>
            <v-tab>時間帯別</v-tab>
            <v-tab>ヒートマップ</v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab">
            <!-- 曜日別 -->
            <v-tab-item>
              <v-simple-table dense>
                <template #default>
                  <thead>
                    <tr>
                      <th>曜日</th>
                      <th class="text-right">
                        来店数
                      </th>
                      <th style="width: 60%;">
                        グラフ
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="day in byDayOfWeek"
                      :key="day.dayOfWeek"
                    >
                      <td>{{ day.label }}曜日</td>
                      <td class="text-right">
                        {{ day.count }}
                      </td>
                      <td>
                        <div
                          class="inline-bar"
                          :style="{ width: getBarWidth(day.count, maxDayCount) }"
                        />
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-tab-item>

            <!-- 時間帯別 -->
            <v-tab-item>
              <v-simple-table dense>
                <template #default>
                  <thead>
                    <tr>
                      <th>時間帯</th>
                      <th class="text-right">
                        来店数
                      </th>
                      <th style="width: 60%;">
                        グラフ
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="hour in byHour"
                      :key="hour.hour"
                    >
                      <td>{{ hour.label }}</td>
                      <td class="text-right">
                        {{ hour.count }}
                      </td>
                      <td>
                        <div
                          class="inline-bar"
                          :style="{ width: getBarWidth(hour.count, maxHourCount) }"
                        />
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-tab-item>

            <!-- ヒートマップ -->
            <v-tab-item>
              <div class="heatmap-container">
                <div class="heatmap-header">
                  <div class="heatmap-corner" />
                  <div
                    v-for="hour in 24"
                    :key="hour"
                    class="heatmap-hour-label"
                  >
                    {{ hour - 1 }}
                  </div>
                </div>
                <div
                  v-for="dayIndex in 7"
                  :key="dayIndex"
                  class="heatmap-row"
                >
                  <div class="heatmap-day-label">
                    {{ DAY_LABELS[dayIndex - 1] }}
                  </div>
                  <div
                    v-for="hourIndex in 24"
                    :key="hourIndex"
                    class="heatmap-cell"
                    :style="{ backgroundColor: getHeatmapCellColor(dayIndex - 1, hourIndex - 1) }"
                    :title="`${DAY_LABELS[dayIndex - 1]}曜日 ${hourIndex - 1}時: ${getHeatmapCount(dayIndex - 1, hourIndex - 1)}回`"
                  />
                </div>
              </div>
              <div class="heatmap-legend mt-2">
                <span class="text-caption mr-2">少</span>
                <div
                  class="legend-box"
                  style="background-color: #f5f5f5;"
                />
                <div
                  class="legend-box"
                  style="background-color: #c8e6c9;"
                />
                <div
                  class="legend-box"
                  style="background-color: #81c784;"
                />
                <div
                  class="legend-box"
                  style="background-color: #4caf50;"
                />
                <div
                  class="legend-box"
                  style="background-color: #2e7d32;"
                />
                <span class="text-caption ml-2">多</span>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { ref, computed } from '@vue/composition-api'
import { mdiChartTimeline, mdiClose } from '@mdi/js'
import { formatDate } from '@/utils/formatters'
import useVisitPattern, { DAY_LABELS } from '../composables/useVisitPattern'

export default {
  name: 'VisitPattern',
  props: {
    dateRange: {
      type: Object,
      default: () => ({ start: null, end: null }),
    },
  },
  setup(props, { root }) {
    const {
      byDayOfWeek,
      byHour,
      heatmap,
      summary,
      loading,
      startDate,
      endDate,
      peakDayInfo,
      peakHourInfo,
      fetchVisitPattern,
      getHeatmapColor,
    } = useVisitPattern(root)

    const showDetailDialog = ref(false)
    const activeTab = ref(0)

    const isLoading = computed(() => loading.value)

    const maxDayCount = computed(() => {
      if (!byDayOfWeek.value.length) return 1

      return Math.max(...byDayOfWeek.value.map(d => d.count), 1)
    })

    const maxHourCount = computed(() => {
      if (!byHour.value.length) return 1

      return Math.max(...byHour.value.map(h => h.count), 1)
    })

    const getBarHeight = count => {
      if (maxDayCount.value === 0) return '4px'
      const maxHeight = 56 // px
      const height = (count / maxDayCount.value) * maxHeight

      return `${Math.max(height, 4)}px`
    }

    const getBarWidth = (count, max) => {
      if (max === 0) return '0%'
      const percentage = (count / max) * 100

      return `${percentage}%`
    }

    const getHeatmapCellColor = (dayOfWeek, hour) => {
      const cell = heatmap.value.find(h => h.dayOfWeek === dayOfWeek && h.hour === hour)

      return getHeatmapColor(cell ? cell.count : 0)
    }

    const getHeatmapCount = (dayOfWeek, hour) => {
      const cell = heatmap.value.find(h => h.dayOfWeek === dayOfWeek && h.hour === hour)

      return cell ? cell.count : 0
    }

    const loadVisitPattern = () => {
      fetchVisitPattern({
        start: props.dateRange.start,
        end: props.dateRange.end,
      })
    }

    // 初回ロード
    loadVisitPattern()

    return {
      byDayOfWeek,
      byHour,
      heatmap,
      summary,
      isLoading,
      startDate,
      endDate,
      peakDayInfo,
      peakHourInfo,
      showDetailDialog,
      activeTab,
      maxDayCount,
      maxHourCount,
      formatDate,
      getBarHeight,
      getBarWidth,
      getHeatmapCellColor,
      getHeatmapCount,
      DAY_LABELS,
      icons: {
        mdiChartTimeline,
        mdiClose,
      },
    }
  },
}
</script>

<style scoped>
.day-chart {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  height: 80px;
  padding: 0 8px;
}

.day-bar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  width: 30px;
  height: 60px;
}

.day-bar {
  width: 20px;
  background-color: #4CAF50;
  border-radius: 4px 4px 0 0;
  min-height: 4px;
  transition: height 0.3s ease;
}

.day-label {
  font-size: 12px;
  margin-top: 4px;
  color: #666;
}

.inline-bar {
  height: 16px;
  background-color: #4CAF50;
  border-radius: 2px;
  min-width: 2px;
}

.heatmap-container {
  overflow-x: auto;
  margin-top: 16px;
}

.heatmap-header {
  display: flex;
}

.heatmap-corner {
  width: 30px;
  height: 20px;
}

.heatmap-hour-label {
  width: 20px;
  height: 20px;
  font-size: 10px;
  text-align: center;
  color: #666;
}

.heatmap-row {
  display: flex;
}

.heatmap-day-label {
  width: 30px;
  height: 20px;
  font-size: 12px;
  display: flex;
  align-items: center;
  color: #666;
}

.heatmap-cell {
  width: 20px;
  height: 20px;
  border: 1px solid #fff;
  cursor: pointer;
}

.heatmap-legend {
  display: flex;
  align-items: center;
  justify-content: center;
}

.legend-box {
  width: 20px;
  height: 20px;
  margin: 0 2px;
  border: 1px solid #ddd;
}
</style>
